import type {
  ImpersonateRequest,
  ImpersonateResponse,
  ImpersonateEndRequest,
  LoginRequest,
  LoginResponse,
  MeResponse,
  PasswordResetConfirm,
  PasswordResetRequest,
} from '@app/shared/schemas/auth';
import { apiRequest } from './client';

/**
 * Auth API surface — thin wrappers around the REST endpoints exposed by
 * `apps/backend/src/routes/v1/auth.ts`. These exist so React Query hooks have
 * stable, typed call sites and so tests can mock per-method.
 */

export function login(payload: LoginRequest): Promise<LoginResponse> {
  return apiRequest<LoginResponse>({
    method: 'POST',
    url: '/auth/login',
    data: payload,
  });
}

export function logout(): Promise<void> {
  return apiRequest<undefined>({
    method: 'POST',
    url: '/auth/logout',
  }).then(() => undefined);
}

export function refresh(): Promise<{ access_token: string; access_token_expires_at: string }> {
  return apiRequest({
    method: 'POST',
    url: '/auth/refresh',
  });
}

export function me(): Promise<MeResponse> {
  return apiRequest<MeResponse>({
    method: 'GET',
    url: '/auth/me',
  });
}

/**
 * Start impersonation ("view as partner") — admin/decision_maker only.
 * Returns a short-lived, access-only token scoped to the target partner.
 * No refresh cookie is set, so the caller's own session is untouched and
 * ending impersonation is a normal `refresh()`. See ADR-0029.
 */
export function impersonate(payload: ImpersonateRequest): Promise<ImpersonateResponse> {
  return apiRequest<ImpersonateResponse>({
    method: 'POST',
    url: '/auth/impersonate',
    data: payload,
  });
}

/**
 * Audit-close an impersonation session. The real session restore is done by
 * calling `refresh()` (the original user's cookie); this only writes the
 * matching audit-log entry, attributing it to the original actor id.
 */
export function endImpersonation(payload: ImpersonateEndRequest): Promise<void> {
  return apiRequest<undefined>({
    method: 'POST',
    url: '/auth/impersonate/end',
    data: payload,
  }).then(() => undefined);
}

/**
 * Request a password reset email. The backend returns 202 regardless of
 * whether the email belongs to a real account (prevents account enumeration);
 * the SPA shows the same confirmation message in either case.
 */
export function forgotPassword(payload: PasswordResetRequest): Promise<void> {
  return apiRequest<undefined>({
    method: 'POST',
    url: '/auth/forgot-password',
    data: payload,
  }).then(() => undefined);
}

/**
 * Complete a password reset using the token delivered by email. The token
 * lives in the request body (not the URL) so it never appears in access
 * logs — same pattern as invitation acceptance.
 */
export function resetPassword(payload: PasswordResetConfirm): Promise<void> {
  return apiRequest<undefined>({
    method: 'POST',
    url: '/auth/reset-password',
    data: payload,
  }).then(() => undefined);
}
